export function isWebAuthnSupported() {
    if (typeof window === 'undefined' || !window.PublicKeyCredential || !navigator.credentials) {
        return false;
    }

    const host = window.location && window.location.hostname ? window.location.hostname : '';
    const localHost = ['localhost', '127.0.0.1', '::1'].indexOf(host) !== -1;

    return !!(window.isSecureContext || localHost);
}

export async function createPasskey(publicKey) {
    const credential = await navigator.credentials.create({
        publicKey: normalizeCreationOptions(publicKey)
    });

    return serializeCredential(credential);
}

export async function authenticatePasskey(publicKey) {
    const credential = await navigator.credentials.get({
        publicKey: normalizeRequestOptions(publicKey)
    });

    return serializeCredential(credential);
}

function normalizeCreationOptions(publicKey) {
    const options = Object.assign({}, publicKey || {});
    options.challenge = base64ToBuffer(options.challenge);

    if (options.user && options.user.id) {
        options.user = Object.assign({}, options.user, {
            id: base64ToBuffer(options.user.id)
        });
    }

    if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map(normalizeCredentialDescriptor);
    }

    if (isNativeIOS()) {
        options.authenticatorSelection = Object.assign({}, options.authenticatorSelection || {}, {
            authenticatorAttachment: 'platform'
        });
    }

    return options;
}

function normalizeRequestOptions(publicKey) {
    const options = Object.assign({}, publicKey || {});
    options.challenge = base64ToBuffer(options.challenge);

    if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map(normalizeCredentialDescriptor);
    }

    return options;
}

function normalizeCredentialDescriptor(item) {
    return Object.assign({}, item, {
        id: base64ToBuffer(item.id)
    });
}

function serializeCredential(credential) {
    if (!credential) {
        return null;
    }

    const response = credential.response || {};
    const serialized = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {},
        clientExtensionResults: credential.getClientExtensionResults
            ? credential.getClientExtensionResults()
            : {}
    };

    [
        'clientDataJSON',
        'attestationObject',
        'authenticatorData',
        'signature',
        'userHandle'
    ].forEach(key => {
        if (response[key]) {
            serialized.response[key] = bufferToBase64url(response[key]);
        }
    });

    if (response.getTransports) {
        serialized.response.transports = response.getTransports();
    }

    return serialized;
}

function base64ToBuffer(value) {
    const normalized = String(value || '')
        .replace(/-/g, '+')
        .replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - normalized.length % 4) % 4);
    const binary = window.atob(padded);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }

    return bytes.buffer;
}

function bufferToBase64url(buffer) {
    const bytes = new Uint8Array(buffer || []);
    let binary = '';

    bytes.forEach(byte => {
        binary += String.fromCharCode(byte);
    });

    return window.btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/g, '');
}

function isNativeIOS() {
    const cfg = (typeof window !== 'undefined' && window.config) ? window.config : {};
    const device = String(cfg.device || '').toLowerCase();

    return cfg.is_native_app === true && (device === 'ios' || device === 'iphone' || device === 'ipad');
}
